import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { colors } from "#app/theme/v1/colors";
import { useWeldLength } from "#modules/part/view/useWeldLength";
import { usePart } from "#modules/parts/view/usePart";
import { AnalyzeError } from "#modules/quoteRequestLines/AnalyzeError";
import { AnalyzeLoader } from "#modules/quoteRequestLines/AnalyzeLoader";
import { useCheckPart } from "#modules/similarPart/view/useCheckPart";
import { Icon } from "#shared/icons/Icon";
import { Flex } from "#shared/view/components/Flex";
import { Stack } from "#shared/view/components/Stack";
import { Text } from "#shared/view/components/Text";
import { Button } from "#shared/view/designSystem/Button";
import { isJobProcessing, JobStatusSchema, round } from "shared";

type Props = {
  partId: string | undefined;
};

export const WeldLength = ({ partId }: Props) => {
  const { t } = useTranslation();
  const [hasRequestedComputation, setHasRequestedComputation] = useState(false);
  const { weldLength, dataUpdatedAt } = useWeldLength(partId);
  const { part } = usePart(partId);
  const { checkPart, isPending: isCheckingPart, isError: hasCheckFailed } = useCheckPart();
  const requestedAtRef = useRef(0);

  if (!partId) {
    return null;
  }

  const computeWeldLength = () => {
    requestedAtRef.current = Date.now();
    setHasRequestedComputation(true);
    checkPart({
      id: partId,
      analyzeType: "COMPUTE_WELD_LENGTH",
      assemblySplitMode: "ROOT",
      shouldUpdatePartType: false,
    });
  };

  const featuresStatus = part?.featuresStatus;
  const hasFailed =
    hasCheckFailed || (hasRequestedComputation && featuresStatus === JobStatusSchema.enum.ERROR);
  const hasSettledWeldLength = dataUpdatedAt > requestedAtRef.current;
  const isWaitingForWeldLength = hasRequestedComputation && !hasFailed && !hasSettledWeldLength;
  const isComputing =
    isCheckingPart ||
    isWaitingForWeldLength ||
    (!!featuresStatus && isJobProcessing(featuresStatus));

  const renderContent = () => {
    if (weldLength !== null) {
      return (
        <Flex gap="xs" align="flex-start">
          <Flex h={20}>
            <Icon name="weld" size={16} />
          </Flex>
          <Text component="span" c={colors.violet[5]}>
            {weldLength > 0
              ? t("quoteRequestLine.weldLength.detected", { length: round(weldLength, 0) })
              : t("quoteRequestLine.weldLength.none")}
          </Text>
        </Flex>
      );
    }
    if (isComputing) {
      return <AnalyzeLoader text={t("quoteRequestLine.weldLength.computing")} />;
    }
    if (hasFailed) {
      return (
        <AnalyzeError
          retryAction={computeWeldLength}
          retryText={t("qrl.analyze.relaunch")}
          errorText={t("quoteRequestLine.weldLength.error")}
          justify="start"
        />
      );
    }
    return (
      <Stack gap="xs" align="flex-start">
        <Text c={colors.gray[5]}>{t("quoteRequestLine.weldLength.notComputed")}</Text>
        <Button variant="light" onClick={computeWeldLength}>
          {t("quoteRequestLine.weldLength.compute")}
        </Button>
      </Stack>
    );
  };

  return (
    <Stack gap="0">
      <Text fw={700} c={colors.violet[5]}>
        {t("quoteRequestLine.weldLength.title")}
      </Text>
      {renderContent()}
    </Stack>
  );
};
